/**
 * HOW THIS DEVICE LAST SIGNED IN - never a token.
 *
 * Gordon: "when I click on the desktop app it is still asking me whether I
 * use the Microsoft login or PIN even if I have accessed Jarvis and closed it
 * off multiple times per day."
 *
 * The front door could not know: Microsoft's own cache (lib/auth) went with
 * the window, so every launch of the installed app was a stranger at the
 * door. This remembers the one thing the door needs to skip the question -
 * WHICH WAY this device came in last, and as whom - so the app can try to
 * sign that person straight back in, and failing that offer "Continue as
 * Rita" as one press.
 *
 * What is kept, in localStorage under one key:
 *   { method: 'microsoft', hint: 'rita@ncmb.eu', name: 'Rita Gatt' }
 *   { method: 'code', email: 'marco@fixit.com' }
 * The address is a login HINT, the name is what the button says. Neither is
 * a credential: no token, code or session ever goes in here - Microsoft's
 * tokens stay in MSAL's own cache, a contractor's session where it always
 * was - and the server checks every request as before. Signing out on
 * purpose forgets it, so the next person at a shared computer gets the
 * plain choice, not somebody else's name.
 *
 * Every read and write is guarded: a private window or blocked storage
 * simply means nothing is remembered and the door asks, as it did.
 */
const KEY = 'jarvis-sign-in';
const EMAIL_RE = /^[^\s@]+@[^\s@]+$/;

const clean = (v, n = 320) => (typeof v === 'string' ? v.trim().slice(0, n) : '');

/** What this device remembers, or null. */
export function rememberedSignIn() {
  let raw = null;
  try { raw = localStorage.getItem(KEY); } catch { return null; }
  if (!raw) return null;
  try {
    const v = JSON.parse(raw);
    if (v && v.method === 'microsoft' && clean(v.hint)) {
      return { method: 'microsoft', hint: clean(v.hint), name: clean(v.name, 120) || null };
    }
    if (v && v.method === 'code' && EMAIL_RE.test(clean(v.email))) {
      return { method: 'code', email: clean(v.email) };
    }
  } catch { /* not ours, or damaged: as good as nothing */ }
  return null;
}

const write = (v) => {
  try { localStorage.setItem(KEY, JSON.stringify(v)); } catch { /* nothing remembered, nothing lost */ }
};

/** Signed in with Microsoft as this account (MSAL's AccountInfo: username, name). */
export function rememberMicrosoft(account) {
  const hint = clean(account?.username);
  if (!hint) return;
  const name = clean(account?.name, 120) || null;
  const was = rememberedSignIn();
  if (was && was.method === 'microsoft' && was.hint === hint && was.name === name) return;
  write({ method: 'microsoft', hint, name });
}

/** Signed in with an emailed code, at this address. */
export function rememberCode(email) {
  const e = clean(email);
  if (EMAIL_RE.test(e)) write({ method: 'code', email: e });
}

/**
 * Forget it - on an explicit Sign out. With a method, only if that is the
 * one remembered: a contractor signing out does not forget a member of
 * staff who uses the same machine, and the other way round.
 */
export function forgetSignIn(method = null) {
  try {
    if (method) {
      const was = rememberedSignIn();
      if (!was || was.method !== method) return;
    }
    localStorage.removeItem(KEY);
  } catch { /* nothing to forget */ }
}

/** The first name, for "Welcome back, Rita". */
export function firstName(name) {
  const n = clean(name, 120);
  if (!n) return null;
  return n.split(/[\s,]+/)[0] || null;
}
